import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { storageKeys } from '@/config/env';

/**
 * How long the cross-fade between themes is allowed to run.
 *
 * Must stay a little longer than `--theme-transition-duration` in
 * styles/index.css — the class has to outlive the animation it enables, or the
 * last few frames snap.
 */
const TRANSITION_CLEANUP_MS = 700;

export type Theme = 'light' | 'dark' | 'system';

interface ThemeContextValue {
  theme: Theme;
  /** What is actually on screen once 'system' is resolved. */
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredTheme(): Theme {
  const stored = localStorage.getItem(storageKeys.theme);
  return stored === 'light' || stored === 'dark' || stored === 'system'
    ? stored
    : 'system';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);
  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const resolvedTheme = theme === 'system' ? (systemDark ? 'dark' : 'light') : theme;

  // The very first run is the initial paint, which must not animate — the
  // inline script in index.html has already applied the right theme, and
  // fading in from the default would be its own flash.
  const isFirstApply = useRef(true);

  useEffect(() => {
    const root = document.documentElement;

    const apply = () => {
      root.classList.toggle('dark', resolvedTheme === 'dark');
      root.style.colorScheme = resolvedTheme;
    };

    if (isFirstApply.current) {
      isFirstApply.current = false;
      apply();
      return;
    }

    // Enabling the transition and changing the colours in one style
    // recalculation does not animate: a transition only runs when the property
    // already had `transition-property` set the last time styles were
    // computed. Reading `offsetHeight` forces that computation in between.
    root.classList.add('theme-transition');
    void root.offsetHeight;
    apply();

    const timer = window.setTimeout(
      () => root.classList.remove('theme-transition'),
      TRANSITION_CLEANUP_MS
    );
    return () => window.clearTimeout(timer);
  }, [resolvedTheme]);

  const setTheme = useCallback((next: Theme) => {
    localStorage.setItem(storageKeys.theme, next);
    setThemeState(next);
  }, []);

  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme]
  );

  return <ThemeContext value={value}>{children}</ThemeContext>;
}

export function useTheme() {
  const context = use(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside <ThemeProvider>');
  return context;
}
